// Libraries
import React from 'react';

// Supermove
import {Styled, DataLoader} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {DrawerType, useQuery, useMemo, useEffect, useRef, useModal} from '@supermove/hooks';
import {AccountGrantModel, OrganizationModel, UserModel} from '@supermove/models';

// App
import DrawerWithAction from '@shared/design/components/Drawer/DrawerWithAction';
import DropdownInput from '@shared/design/components/DropdownInput';
import FieldInput from '@shared/design/components/Field/FieldInput';
import AccountGrantAssignmentsForm, {
  AccountGrantAssignmentsFormFields,
} from '@shared/modules/AccountGrant/forms/AccountGrantAssignmentsForm';
import useUpsertAccountGrantAssignmentsMutation, {
  UpsertAccountGrantAssignmentsFormType,
} from '@shared/modules/AccountGrant/hooks/useUpsertAccountGrantAssignmentsMutation';
import QuitEditingDrawerModal from 'modules/Organization/Settings/Communication/components/CommunicationAccounts/components/QuitEditingDrawerModal';
import {sortAccountGrants} from 'modules/Organization/Settings/Communication/components/CommunicationAccounts/components/sortAccountGrants';
import {sortBranches} from 'modules/Organization/Settings/Communication/components/CommunicationAccounts/sortBranches';

const LoadingIndicator = Styled.Loading`
`;

const ContentContainer = Styled.View`
  flex: 1;
`;

const OrganizationContainer = Styled.View<{index: number}>`
  padding-bottom: 16px;
  z-index: ${({index}) => 100 - index};
`;

const EditBranchAccountContent = ({
  organizations,
  accountGrants,
  form,
  field,
}: {
  organizations: OrganizationModel[];
  accountGrants: AccountGrantModel[];
  form: UpsertAccountGrantAssignmentsFormType;
  field: string;
}) => {
  const accountGrantOptions = accountGrants.sort(sortAccountGrants).map((accountGrant) => ({
    label: accountGrant.identifier,
    value: accountGrant.id,
    description: accountGrant.isPrimary ? 'Primary' : undefined,
  }));

  const sortedOrganizations = useMemo(
    () => organizations.filter((organization) => !organization.isDeleted).sort(sortBranches),
    [organizations],
  );

  return (
    <ContentContainer>
      {sortedOrganizations.map((organization, listIndex) => {
        // The index on the form doesn't match the index of the sorted orgs, so find the form index
        const formIndex = form.values.accountGrantAssignmentsForms.findIndex(
          (form) => form.organizationId === organization.id,
        );
        return (
          <OrganizationContainer key={organization.id} index={listIndex}>
            <FieldInput
              {...form}
              name={`${field}.${formIndex}.accountGrantId`}
              label={organization.name}
              isRequired
              component={DropdownInput}
              // Always show error messages
              touched={{[`${field}.${formIndex}.accountGrantId`]: true}}
              input={{
                options: accountGrantOptions,
                showDescriptionInOption: true,
                setFieldValue: form.setFieldValue,
                style: {width: '100%'},
              }}
            />
          </OrganizationContainer>
        );
      })}
    </ContentContainer>
  );
};

const getInitialValues = (
  organizations?: OrganizationModel[],
): AccountGrantAssignmentsFormFields[] => {
  return (
    organizations
      ?.filter((organization) => !organization.isDeleted)
      .map((organization) => {
        const assignments = organization.accountGrantOrganizationAssignments?.[0];
        if (assignments) {
          return AccountGrantAssignmentsForm.edit(assignments);
        }
        return AccountGrantAssignmentsForm.new({organizationId: organization.id});
      }) ?? []
  );
};

const EditBranchAccountsDrawer = ({drawer, refetch}: {drawer: DrawerType; refetch: () => void}) => {
  const quitEditingModal = useModal({
    name: 'Quit Editing Account Grant Assignments Drawer Modal',
  });
  const initialState = useRef<{
    accountGrantAssignmentsForms: AccountGrantAssignmentsFormFields[];
  } | null>(null);
  const {data, loading} = useQuery<{viewer: UserModel}>(EditBranchAccountsDrawer.query, {
    fetchPolicy: 'cache-and-network',
    skip: !drawer.isOpen,
  });

  const {form, submitting, handleSubmit} = useUpsertAccountGrantAssignmentsMutation({
    accountGrantAssignmentsForms: getInitialValues(
      data?.viewer.viewingOrganization?.allOrganizations,
    ),
    refetchQueries: ['OrganizationSettingsCommunicationAccountsTable'],
    onSuccess: () => {
      drawer.handleClose();
      refetch();
    },
    onError: (errors) => console.log('errors', errors),
  });

  // Reset form when drawer is opened
  useEffect(() => {
    if (drawer.isOpen) {
      const initialValues = {
        accountGrantAssignmentsForms: getInitialValues(
          data?.viewer.viewingOrganization?.allOrganizations,
        ),
      };
      form.setValues(initialValues);
      initialState.current = initialValues;
    } else {
      form.resetForm();
    }
  }, [drawer.isOpen, data?.viewer.viewingOrganization?.allOrganizations]);

  const handleClose = () => {
    if (
      form.values.accountGrantAssignmentsForms !==
      initialState.current?.accountGrantAssignmentsForms
    ) {
      quitEditingModal.handleOpen();
    } else {
      drawer.handleClose();
    }
  };

  return (
    <DrawerWithAction
      isOpen={drawer.isOpen}
      handleClose={handleClose}
      headerText={'Default for Branches'}
      description={'Select a default email account for each branch when sending emails.'}
      primaryActionText={'Save'}
      secondaryActionText={'Cancel'}
      secondaryAction={handleClose}
      isSubmitting={submitting}
      handleSubmit={handleSubmit}
    >
      <DataLoader loading={!data || loading} data={data} as={LoadingIndicator}>
        {({loadedData}) => (
          <EditBranchAccountContent
            accountGrants={loadedData.viewer.viewingOrganization.activeAccountGrants}
            organizations={loadedData.viewer.viewingOrganization.allOrganizations}
            form={form}
            field={'accountGrantAssignmentsForms'}
          />
        )}
      </DataLoader>
      <QuitEditingDrawerModal
        key={quitEditingModal.key}
        isOpen={quitEditingModal.isOpen}
        handleClose={quitEditingModal.handleClose}
        handleQuitEditing={() => {
          quitEditingModal.handleClose();
          drawer.handleClose();
        }}
      />
    </DrawerWithAction>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
EditBranchAccountsDrawer.query = gql`

  query OrganizationSettingsEditBranchAccountsDrawer {
    ${gql.query}
    viewer {
      id
      viewingOrganization {
        id
        activeAccountGrants {
          id
          isPrimary
          identifier
          accountGrantOrganizationAssignments {
            id
            organization {
              id
              isDeleted
            }
          }
        }
        allOrganizations {
          id
          name
          isDeleted
          accountGrantOrganizationAssignments {
            id
            accountGrantId
            organizationId
          }
        }
      }
    }
  }
`;

export default EditBranchAccountsDrawer;
